/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Handle, Position } from 'reactflow';
import { ChevronRight } from 'lucide-react';
import { GenericCard } from '../../../../../ui-components/Card/GenericCard';
import { GenericTag } from '../../../../../ui-components/Tag/GenericTag';
import { BgpNeighborData } from '../../BgpVisibilityData';
import { getStatusVariant } from '../../../../helpers/bgpValidation';
import { TOPOLOGY_CARD_LAYOUT } from '../../utils/graphGeneration';
import { BGP_EDGE_COLORS } from '../../styles/bgpVisibilityStyles';

interface BgpNeighborCardProps {
  data: BgpNeighborData & {
    /** Distinct prefixes announced by this neighbor. */
    prefixCount?: number;
    /** Distinct route tables (inbound) / subnets (outbound) this neighbor reaches. */
    routeTableCount?: number;
    bestCount?: number;
    withdrawnCount?: number;
    /**
     * This peer is reported DOWN and exchanges nothing — it exists on the canvas only because the
     * neighbor roster reports it, not because any route led here. Route counts and the
     * "click to view prefixes" CTA are meaningless for it, so the card swaps them for the down
     * treatment instead of rendering a row of confident zeroes.
     */
    isDown?: boolean;
    /** The reported FSM state behind `isDown` (e.g. 'Idle'), shown in place of the counts. */
    sessionStatus?: string;
  };
  onCardClick?: (data: BgpNeighborData) => void;
  isOutbound?: boolean;
  neighborInfo?: {
    peerAs?: number;
    description?: string;
    operatingMode?: string;
    status?: string;
    statusChangedAt?: number;
  };
}

/**
 * Compact neighbor node: shows the announced-prefix COUNT (not each prefix), keeping the
 * node a fixed, bounded size at any volume. Clicking it opens the neighbor modal with the
 * full prefix list. A single right-edge handle anchors the aggregated per-route-table edges.
 */
export const BgpNeighborCard: React.FC<BgpNeighborCardProps> = ({
  data,
  onCardClick,
  isOutbound = false,
  neighborInfo
}) => {
  const { neighborIp } = data;
  const isDown = data.isDown === true;
  const prefixCount = data.prefixCount ?? data.prefixes?.length ?? 0;
  const routeTableCount = data.routeTableCount ?? 0;
  const bestCount = data.bestCount ?? 0;
  const withdrawnCount = data.withdrawnCount ?? 0;

  // The handle is where the eye starts tracing an edge, so it has to agree with the edges it
  // anchors (CTX-106's pink = best / grey = alternate). It was hardcoded pink, which claimed
  // "best path" for peers whose paths had all lost the decision. One handle carries every edge of
  // the peer, so it can only reflect the peer as a whole: pink once anything here won.
  const handleColor = bestCount > 0 ? BGP_EDGE_COLORS.best : BGP_EDGE_COLORS.secondary;

  const formatStatus = (status?: string): string => {
    if (!status) return 'Unknown';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };
  const status = neighborInfo?.status || 'UNKNOWN';
  // A down peer says so plainly in warning yellow. Everything else keeps the shared three-way
  // reading (established → success, reported non-established → danger, unreported → neutral),
  // which the Table view and the detail panels also use.
  const statusLabel = isDown ? 'Down' : formatStatus(status);
  const statusVariant = isDown ? 'warning' : getStatusVariant(status);

  const headerContent = (
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-2">
        <h3 className="font-semibold text-semantic-text-primary">{neighborIp}</h3>
        <GenericTag label={statusLabel} variant={statusVariant} size="small" />
      </div>
    </div>
  );

  // A down peer has no counts worth showing and nothing to open — it states the session state
  // that took it down instead of a row of zeroes and a CTA leading to an empty list.
  //
  // "Status Session Active", not "Session Active": the BGP FSM states that mean *not* established
  // include Active and Connect, which read as healthy in plain English. Leading with the label is
  // what stops the line contradicting the Down badge right above it.
  const detailBlock = isDown ? (
    <div className="text-sm text-semantic-text-warning">
      Status Session {data.sessionStatus || 'down'} · no prefixes 
    </div>
  ) : (
    <>
      <div className="flex flex-wrap items-center gap-1.5">
        <GenericTag label={`${prefixCount} ${prefixCount === 1 ? 'prefix' : 'prefixes'}`} variant="neutral" size="small" />
        <GenericTag label={`${routeTableCount} ${isOutbound ? 'Subnets' : 'RTs'}`} variant="neutral" size="small" />
        {bestCount > 0 && <GenericTag label={`${bestCount} best`} variant="success" size="small" />}
        {withdrawnCount > 0 && <GenericTag label={`${withdrawnCount} withdrawn`} variant="error" size="small" />}
      </div>

      <div className="flex items-center gap-1 text-sm font-medium text-semantic-text-brand">
        <span>Click to view prefixes</span>
        <ChevronRight size={14} />
      </div>
    </>
  );

  const bodyContent = (
    <div className="space-y-3" style={{ minWidth: 220 }}>
      <div className="text-sm text-semantic-text-secondary">
        AS {neighborInfo?.peerAs || '—'}{neighborInfo?.description ? ` • ${neighborInfo.description}` : ''}
      </div>

      {detailBlock}
    </div>
  );

  return (
    <div
      className={`relative ${isDown ? 'cursor-default' : 'cursor-pointer'}`}
      style={{ pointerEvents: 'auto', minHeight: TOPOLOGY_CARD_LAYOUT.neighborCardH }}
      onClick={isDown ? undefined : () => onCardClick?.(data)}
    >
      <GenericCard
        variant="primary"
        shadow="sm"
        isCollapsible={false}
        headerContent={headerContent}
        bodyContent={bodyContent}
        headerColor={isDown ? 'bg-semantic-bg-warning' : 'bg-semantic-bg-success'}
      />
      {/* Single handle anchoring aggregated per-route-table edges (source inbound, target outbound).
          A down peer has no edges to anchor, so it carries no handle. */}
      {!isDown && (
      <Handle
        type={isOutbound ? 'target' : 'source'}
        position={Position.Right}
        id={`neighbor-handle-${neighborIp}`}
        style={{
          background: handleColor,
          width: 8,
          height: 8,
          border: '2px solid #fff',
          borderRadius: '50%',
          right: -5,
          top: '50%',
          transform: 'translateY(-50%)'
        }}
      />
      )}
    </div>
  );
};
